import { ColumnType, IMappingConfiguration } from '@ingestro/importer-react'
import { DropdownOptionValidation, ValidatorAPI } from '@ingestro/importer-react'
import { NuvoImporterQuery } from 'generated/resources/graphql'

interface UseColumnsMappingProps {
  data?: NuvoImporterQuery
  optionMappingConfiguration?: IMappingConfiguration
  isEdition: boolean
}

type DropdownOption = {
  label: string
  alternativeMatches?: string[]
  description?: string | null
  type: string
  value: string
  validations?: DropdownOptionValidation[] | null
}

// Backend validation structure (from GraphQL) - matches UniversalImporterValidation
type BackendValidation = {
  __typename?: 'UniversalImporterValidation'
  errorMessage: string
  operator: string
  conditions: {
    __typename?: 'UniversalImporterFilterCondition'
    field: string
    values: string[]
  }[]
}

// Convert backend validation to Nuvo format
function toNuvoDropdownValidation(validation: BackendValidation): DropdownOptionValidation {
  const validate: Record<string, Record<string, string[]>> = {}

  validation.conditions.forEach((condition) => {
    if (!validate[validation.operator]) {
      validate[validation.operator] = {}
    }
    const operatorObj = validate[validation.operator]
    if (operatorObj) {
      operatorObj[condition.field] = condition.values
    }
  })

  return {
    validate,
    errorMessage: validation.errorMessage,
  }
}

// Convert backend columnValues array to Nuvo ColumnValueAPI Record format
function convertColumnValuesToRecord(columnValues?: Array<{ column: string; values: string[] }>) {
  return (
    columnValues?.reduce<Record<string, string[]>>((acc, columnValue) => {
      if (columnValue.column && columnValue.values) {
        acc[columnValue.column] = columnValue.values
      }
      return acc
    }, {}) || undefined
  )
}

export type Column = {
  label: string
  key: string
  description?: string | null
  example?: string | null
  columnSize?: number | null
  validations?: ValidatorAPI[] | null
  columnType?: ColumnType | null
  dropdownOptions?: DropdownOption[] | null
  alternativeMatches?: string[]
  isMultiSelect?: boolean
  outputFormat?: string | null
  allowCustomOptions?: boolean
  numberFormat?: 'eu' | 'us'
  columnMappingConfiguration?: IMappingConfiguration
  optionMappingConfiguration?: IMappingConfiguration
  hidden?: boolean
  disabled?: boolean
}

export const useColumnsMapping = ({
  data,
  optionMappingConfiguration,
  isEdition,
}: UseColumnsMappingProps) => {
  // Collect all visible column keys to filter cross-column validation references
  const visibleColumnKeys = new Set(
    data?.universalImporter.schemaAdapters
      .filter((node) => !node.hidden || isEdition)
      .map((node) => node.key) || []
  )

  const columns: Column[] =
    data?.universalImporter.schemaAdapters
      .filter((node) => !node.hidden || isEdition)
      .map(({ __typename, dropdownOptions, validations, customizations: _, ...nuvoField }) => {
        const mappedValidations: ValidatorAPI[] =
          validations?.reduce<ValidatorAPI[]>(
            (acc, { __typename: _validationTypename, ...nuvoValidation }) => {
              // Filter out references to columns that don't exist in the current schema
              // (e.g. identifier_type is excluded for certain countries via capability filters)
              const filteredColumns = nuvoValidation.columns?.filter((col) =>
                visibleColumnKeys.has(col)
              )

              // If a required_with/required_with_all validation has no remaining columns
              // after filtering, skip it entirely to avoid Nuvo errors
              if (
                (nuvoValidation.validate === 'required_with' ||
                  nuvoValidation.validate === 'required_with_all') &&
                filteredColumns &&
                filteredColumns.length === 0
              ) {
                return acc
              }

              acc.push({
                ...nuvoValidation,
                errorMessage: nuvoValidation.errorMessage || '',
                columns: filteredColumns?.length ? filteredColumns : undefined,
                columnValues: convertColumnValuesToRecord(nuvoValidation.columnValues || undefined),
              })
              return acc
            },
            []
          ) || []
        const mappedDropdownOptions =
          dropdownOptions?.map(
            ({
              __typename: __dropdownOptionTypename,
              validations: dropdownValidations,
              ...option
            }) => ({
              ...option,
              validations: dropdownValidations?.map(toNuvoDropdownValidation) || null,
            })
          ) || []

        return {
          ...nuvoField,
          // In edition mode, hidden+disabled fields should be visible but not editable.
          // Without this, Nuvo would hide them entirely.
          hidden: nuvoField.hidden && nuvoField.disabled ? false : nuvoField.hidden,
          dropdownOptions: mappedDropdownOptions,
          validations: mappedValidations,
          isMultiSelect: nuvoField.isMultiSelect == true,
          optionMappingConfiguration,
        }
      }) || []
  return { columns }
}
